<template>
	<main>
		<header>
			<pageHeader1image v-bind="pageHeader">
				<div id="booking-widget" class="flex items-center tabletWide:items-start desktop:items-center tabletWide:flex-col desktop:flex-row tablet:space-x-32 tabletWide:space-x-16 tabletWide:space-y-32 desktop:space-y-0 ">
					<formItinerary theme="ghost" class="bg-white/40 rounded-GO p-16 w-full tablet:w-8/12 tabletWide:w-full desktop:w-10/12 desktop:p-24 desktopWide:w-9/12" :branchPickerStartCode="pageContent.PickUpBranch" :branchPickerEndCode="pageContent.DropOffBranch" />
				</div>
			</pageHeader1image>
		</header>

		<div class="py-16 tablet:py-48 desktop:py-56 space-y-56 tabletWide:space-y-112">
			<LazyHydrate when-visible>
				<layoutSection>
					<div class="grid grid-cols-12 gap-8 tablet:gap-16">
						<div class="col-span-12 tabletWide:col-span-8">
							<div :class="{'tabletWide:w-10/12': pageContent.stats.length}" class="p-16 tablet:px-48 tablet:pt-0 tabletWide:p-0">
								<div class="flex flex-col space-y-32 mb-32" v-html="pageContent.content" />
								<button class="button button-goPinkGhost button-normal" @click.prevent="$scrollTo('trip-itinerary')">See trip itinerary</button>
							</div>
						</div>
						<div v-if="pageContent.stats.length" class="col-span-12 tabletWide:col-span-4 p-16 tablet:p-0">
							<div class="space-y-24 p-32 bg-goPinkBrighter rounded-6 border border-goPink/5 shadow-sm">
								<h3 class="font-bold text-20 tablet:text-24 text-goPink">Quick Stats</h3>
								<ul class="space-y-24">
									<li v-for="stat in pageContent.stats" :key="stat.id" class="flex items-center gap-16">
										<svg-icon :name="stat.icon" class="iconSize-32 text-goPink" />
										<p>
											<strong class="font-bold">{{ stat.title }}:</strong> <span v-html="stat.value" />
										</p>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-if="pageContent.map.enabled" when-visible>
				<layoutSection>
					<h2 class="px-16 tablet:p-0 font-bold text-20 tablet:text-24 mb-32">Road Trip Map</h2>
					<div class="tablet:rounded-16 overflow-hidden shadow-sm">
						<iframe :src="pageContent.map.url" width="100%" height="500" loading="lazy" referrerpolicy="no-referrer-when-downgrade" />
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-if="pageContent.video.enabled" when-visible>
				<layoutSection type="post">
					<template #main>
						<layoutPost type="left">
							<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">{{ pageContent.howLongSection.title }}</h2>
							<div class="space-y-16 text-18 desktop:text-22" v-html="pageContent.howLongSection.content" />
						</layoutPost>
					</template>
					<template #aside>
						<figure>
							<videoYouTube
								:title="`${pageContent.navTitle} road trip video`"
								:src="pageContent.video.url"
								:image="pageContent.video.image"
							/>
						</figure>
					</template>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-else-if="pageContent.howLongSection.content" when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">{{ pageContent.howLongSection.title }}</h2>
						<div class="space-y-16 text-18 desktop:text-22" v-html="pageContent.howLongSection.content" />
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-if="pageContent.staffTips.length" when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32 tablet:w-8/12">
							<h2 class="font-bold text-32 tablet:text-40 desktop:text-48 text-goPink">{{ `${pageContent.navTitle} Tips from our Local Staff!` }}</h2>
							<p class="text-18 desktop:text-22 text-goGrayDarker">Kia Ora from our award-winning employees! We are passionate about New Zealand and have put together our insider local tips to GO the extra mile for our customers!</p>
						</div>
						<ul class="flex overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-4 desktop:gap-16">
							<li v-for="tip in pageContent.staffTips" :key="tip.id" class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<article class=" flex flex-col h-full border bg-goPinkBrighter rounded-6 border-goPink/5 overflow-hidden">
									<figure>
										<goPicture v-bind="tip.image" class="w-full" />
									</figure>
									<div class="p-16 space-y-16">
										<h3 class="font-bold text-goPink">{{ tip.title }}</h3>
										<div v-html="tip.content" />
									</div>
								</article>
							</li>
						</ul>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<div id="trip-itinerary" class="tablet:ml-48 tablet:pr-48 desktop:ml-56 desktop:pr-56 space-y-8 tabletWide:space-y-16">
					<div class="grid grid-cols-12 items-start tablet:gap-32 tablet:gap-48 desktop:mx-auto desktop:max-w-1328 p-16 tablet:p-0">
						<roadTripItineraryContents :contents="pageContent.itineraryContents" />
						<div class="col-span-12 tabletWide:col-span-8 desktop:col-span-9 space-y-56 tabletWide:space-y-112">
							<section v-for="(itinerary, index) in pageContent.itineraries" :id="itinerary.id" :key="itinerary.id">
								<h2 class="flex items-center gap-12 font-bold text-22 tablet:text-24 desktop:text-32 mb-24 tablet:mb-32">
									<div class="w-48 h-48 desktop:w-56 desktop:h-56 bg-goPinkBrighter text-goPink rounded-full flex flex-shrink-0 items-center justify-center border border-goPink/5 font-bold">{{ index + 1 }}</div>
									<span>{{ itinerary.title }}</span>
								</h2>
								<div class="grid grid-cols-12 gap-16 tablet:gap-32">
									<div class="col-span-12 desktop:col-span-6">
										<figure class="sticky top-[7.6rem] desktop:pr-8">
											<goPicture v-bind="itinerary.image" class="w-full rounded-GO" />
										</figure>
									</div>
									<div class="col-span-12 desktop:col-span-6">
										<div class="trip-planner-content" v-html="itinerary.content" />
									</div>
								</div>
							</section>
						</div>
					</div>
				</div>
			</LazyHydrate>

			<LazyHydrate v-if="pageContent.drivingTips.content" when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<h2 class="font-bold text-32 desktop:text-48 text-goPink">{{ pageContent.drivingTips.title }}</h2>
						<div class="space-y-32" v-html="pageContent.drivingTips.content" />
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<skiVehicleGroupSections v-if="pageContent.skiContent" v-bind="pageContent.skiContent" />

			<LazyHydrate v-if="pageContent.snapHappyList.length" when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32 tablet:w-8/12">
							<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">{{ `${pageContent.navTitle} Through the Lense of our Customers` }}</h2>
							<p class="text-18 desktop:text-22">Our <nuxt-link to="/go-snap-happy/" class="link link-goBlue font-bold">Snap Happy Photo Competition</nuxt-link> has customers revv’d up about how far they can go in a modern car hire in New Zealand! Submit a cool photo of your GO travel memory and be in with a chance to WIN up to $500 back from your booking too.</p>
						</div>
						<ul class="flex overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-4 desktop:gap-16">
							<li v-for="item in pageContent.snapHappyList" :key="item.id" class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<article class=" flex flex-col h-full border border-goGrayBrighter rounded-6 overflow-hidden">
									<figure>
										<goPicture v-bind="item.image" class="w-full" />
									</figure>
									<div class="p-16 space-y-16">
										<h3 class="font-bold">{{ item.title }}</h3>
										<div v-html="item.content" />
									</div>
								</article>
							</li>
						</ul>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-if="pageContent.oneWayContent" when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<h2 class="font-bold text-24 desktop:text-32">One-Way Rentals Fueling Road Trips</h2>
						<div class="space-y-32" v-html="pageContent.oneWayContent" />
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-if="pageContent.finalSection.content" when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<h2 class="font-bold text-32 desktop:text-48">{{ pageContent.finalSection.title }}</h2>
						<div class="space-y-16" v-html="pageContent.finalSection.content" />
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32">
							<header class="tablet:w-8/12">
								<p class="font-bold text-24 desktop:text-32">Popular GO Rentals cars</p>
							</header>
						</div>
						<vehicleGroupsSection class="grid grid-cols-2 gap-16 tablet:grid-cols-3 desktop:grid-cols-7" />
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-if="pageContent.otherTrips.length" when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 text-center">
						<h2 class="font-bold text-24 desktop:text-32 mb-32">Other Road Trips</h2>
						<ul class="flex overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-4 desktop:gap-16 pb-24 mb-8">
							<li v-for="trip in pageContent.otherTrips" :key="trip.id" class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<a :href="trip.url" class="other-trip-link flex flex-col h-full link link-goBlue border border-black/10 rounded-6 overflow-hidden shadow-sm hover:shadow">
									<figure class="overflow-hidden">
										<goPicture v-bind="trip.image" class="w-full" />
									</figure>
									<p class="px-16 py-24 font-bold text-18">{{ trip.title }}</p>
								</a>
							</li>
						</ul>
						<a href="/explore/trip-planner/" class="button button-goPinkGhost button-normal">See all road trips</a>
					</div>
				</layoutSection>
			</LazyHydrate>
		</div>

		<scrollToTopButton />
	</main>
</template>

<script>
import { marked } from 'marked' // eslint-disable-line

import { getSkiVehicleGroupsData } from "~/assets/scripts/utilities/getSkiVehicleGroupsData"
import layoutSection from "~/components/layoutSection.vue"
import layoutPost from "~/components/layoutPost.vue"
import roadTripItineraryContents from "~/components/roadTripItineraryContents.vue"

export default {
	components: {
		layoutSection,
		layoutPost,
		roadTripItineraryContents,
		pageHeader1image: () => import("~/components/pageHeader1image.vue"),
		videoYouTube: () => import("~/components/videoYouTube.vue"),
		formItinerary: () => import("~/components/formItinerary.vue"),
		vehicleGroupsSection: () => import("~/components/vehicleGroupsSection.vue"),
		scrollToTopButton: () => import("~/components/scrollToTopButton.vue"),
		skiVehicleGroupSections: () => import("~/components/skiVehicleGroupSections.vue"),
	},
	metaInfo() {
		return {
			title: this.pageContent.pageTitle,
			meta: [
				{
					vmid: "description",
					name: "description",
					content: this.pageContent.description,
				},
				{
					vmid: "ogtitle",
					property: "og:title",
					content: this.pageContent.pageTitle,
				},
				{
					vmid: "ogurl",
					property: "og:url",
					content: `https://www.gorentals.co.nz/explore/trip-planner/${this.pageContent.slug}/`,
				},
			],
			link: [{ rel: "canonical", href: `https://www.gorentals.co.nz/explore/trip-planner/${this.pageContent.slug}/` }],
		}
	},
	async asyncData(context) {
		const { slug } = context.route.params
		const tripStrapiComponents = [
			'heroImage',
			'content',
			'Map',
			'howLongSection',
			'video',
			'staff_tips',
			'itineraries',
			'drivingTips',
			'snap_happies',
			'finalSection',
			'other_trips',
		]
		const skiContentSlugs = [
			"north-island-winter-road-trip",
			"south-island-winter-road-trip",
		]

		const tripData = await context.$strapi.$get(`/trip-planners/?filters[slug][$eq]=${slug}&${tripStrapiComponents.map(componet => `populate[${componet}][populate]=*`).join('&')}`)
		if (!tripData?.data?.length) {
			context.error({ statusCode: 404, message: 'Missing trip' })
			return
		}

		let skiContent = null
		if (skiContentSlugs.includes(slug)) {
			const vehicleGroupsContent = {
				awd: "<p>By hiring a <a href='/vehicles/4-wheel-drives/' class='font-bold link link-goBlue'>4WD</a>, you can enjoy a sturdy, reliable vehicle built to handle off-road paths with ease. A 4WD has enough space for up to 5 or 7 people and can also maintain control and stability for an overall safer ride. Drive with confidence and peace of mind by hiring a 4WD for your ski trip today.</p>",
				suv: "<p>SUVs have plenty of legroom for the whole crew and a 5-star ANCAP safety rating for that extra peace of mind on the road.</p>",
				small: "<p>Most mountain locations in New Zealand are easy to get to with a compact 2WD and snow chains if necessary. A <a href='/vehicles/small-cars/' class='font-bold link link-goBlue'>compact 2WD</a> is ideal for 2-3 people with minimal luggage and who are confident drivers. Our compact 2WD ski rental cars are modern and have 5-star ANCAP safety ratings.</p>",
				hybrid: `
					<p>Looking for a fuel-efficient ski trip? GO Rentals <a href='/vehicles/hybrid-cars/' class='font-bold link link-goBlue'>hybrid car hires</a> use an electric battery to take some of the load off the petrol engine and keep your vehicle running for longer on less fuel. Thanks to regenerative braking technology that recharges the battery, GO Rentals hybrid cars don’t even need to be plugged in, just refuelled. Reduce your carbon emissions in comfortable rentals spacious enough for up to 5/7 people.</p>
					<p>Electric vehicles are unfortunately not suitable as ski rentals, as snow chains cannot be fitted onto the wheels.</p>
				`,
				minibus: `
					<p>If you and your ski group are travelling with a few bags each, a <a href='/vehicles/van-or-people-carriers/' class='font-bold link link-goBlue'>minibus rental</a> can provide the comfort, safety and space you need to make it up the long mountain roads. A single minibus instead of multiple rental cars will reduce travel costs and give you enough legroom and luggage space for up to 12 people.</p>
					<p>Many ski resorts also offer easier parking for carpool travellers; be sure to check with the resort in advance for any discounts.</p>
				`,
			}
			skiContent = await getSkiVehicleGroupsData(context, vehicleGroupsContent)
		}

		const tripContent = tripData.data[0].attributes
		const pageHeader = {
			titleTag: 'Road Trip',
			title: tripContent.content.title,
			breadcrumb: {
				theme: "light",
				crumbs: [
					{ href: "/", text: "Home" },
					{ href: "/explore/trip-planner/", text: "Trip Planner" },
					{ text: tripContent.navTitle },
				],
			},
			picture: {
				alt: tripContent.heroImage.altText,
				mobile: { src: tripContent.heroImage.mobileImage.data?.attributes.url, width: 375, height: 495 },
				tablet: { src: tripContent.heroImage.tabletImage.data?.attributes.url, width: 720, height: 720 },
				tabletWide: { src: tripContent.heroImage.tabletWideImage.data?.attributes.url, width: 976, height: 696 },
				desktop: { src: tripContent.heroImage.desktopImage.data?.attributes.url, width: 1384, height: 718 },
				desktopWide: { src: tripContent.heroImage.desktopWideImage.data?.attributes.url, width: 1864, height: 718 },
			},
		}

		const stats = [
			{
				title: 'Trip distance',
				icon: 'route',
				value: tripContent.Distance
			},
			{
				title: 'Days',
				icon: 'calendar-days',
				value: tripContent.Days
			},
			{
				title: 'Car recommendation',
				icon: 'car-side',
				value: tripContent.VehicleRecommendation
					? marked.parseInline(tripContent.VehicleRecommendation).replace(/<a /g, '<a class="link link-goBlue" ')
					: null
			},
			{
				title: 'Suitable for',
				icon: 'thumbs-up',
				value: tripContent.SuitableFor
			}
		].filter(stat => stat.value)

		const pageContent = {
			...tripContent,
			stats,
			skiContent,
			content: marked.parse(tripContent.content.content)
				.replace('<p>', '<p class="text-18 desktop:text-22">')
				.replace(/<a /g, '<a class="link link-goBlue font-bold" '),
			map: {
				enabled: tripContent.Map.Enabled,
				url: tripContent.Map.Url
			},
			video: {
				...tripContent.video,
				image: { mobile: { src: tripContent.video.image.data?.attributes.url }, alt: tripContent.video.image.data?.attributes.alternativeText }
			},
			howLongSection: {
				title: tripContent.howLongSection.title || `How long is the ${tripContent.navTitle} drive?`,
				content: tripContent.howLongSection.content
					? marked.parse(tripContent.howLongSection.content).replace(/<a /g, '<a class="link link-goBlue font-bold" ')
					: null
			},
			staffTips: tripContent.staff_tips.data.map(({ id, attributes }) => ({
				id,
				title: attributes.Title,
				content: marked.parse(attributes.Content),
				image: { mobile: { src: attributes.Image.data?.attributes.url, width: 340, height: 226 }, alt: attributes.Image.data?.attributes.alternativeText }
			})),
			itineraries: tripContent.itineraries.map(itinerary => ({
				id: `itinerary-${itinerary.id}`,
				title: itinerary.Title,
				content: marked.parse(itinerary.Content)
					.replace('<p>', '<p class="text-18 desktop:text-22">')
					.replace(/<p>/g, '<p class="mb-16">')
					.replace(/<h3 /g, '<h3 class="font-bold text-20 desktop:text-24 mt-32 mb-24"')
					.replace(/<h4 /g, '<h3 class="font-bold mt-24 mb-16"')
					.replace(/<a /g, '<a class="link link-goBlue font-bold" '),
				image: { mobile: { src: itinerary.Image.data?.attributes.url, width: 600, height: 400 }, alt: itinerary.Image.data?.attributes.alternativeText }
			})),
			drivingTips: {
				title: tripContent.drivingTips.title || `${tripContent.navTitle} road trip tips?`,
				content: tripContent.drivingTips.content
					? marked.parse(tripContent.drivingTips.content)
						.replace('<p>', '<p class="text-18 desktop:text-22 text-goGrayDarker">')
						.replace('<ul>', '<ul class="text-14 space-y-16 desktop:text-16">')
						.replace(/<li>/g, '<li class="bg-iconChevron pl-32">')
						.replace(/<strong>/g, '<strong class="font-bold">')
						.replace(/<\/strong>/g, '</strong><br />')
						.replace(/<a /g, '<a class="link link-goBlue font-bold" ')
					: null
			},
			snapHappyList: tripContent.snap_happies.data.map(({ id, attributes }) => ({
				id,
				title: attributes.Title,
				content: marked.parse(attributes.Content),
				image: { mobile: { src: attributes.Image.data?.attributes.url, width: 340, height: 285 }, alt: attributes.Image.data?.attributes.alternativeText }
			})),
			oneWayContent: tripContent.oneWayContent
				? marked.parse(tripContent.oneWayContent).replace(/<a /g, '<a class="link link-goBlue font-bold" ')
				: null,
			finalSection: {
				...tripContent.finalSection,
				content: tripContent.finalSection.content
					? marked.parse(tripContent.finalSection.content).replace(/<a /g, '<a class="link link-goBlue font-bold" ')
					: null
			},
			otherTrips: tripContent.other_trips.data.map(({ id, attributes }) => ({
				id,
				title: attributes.Title,
				url: attributes.Url,
				image: { mobile: { src: attributes.Image.data?.attributes.url, width: 340, height: 226 }, alt: attributes.Image.data?.attributes.alternativeText }
			}))
		}
		pageContent.itineraryContents = pageContent.itineraries.map((itinerary, index) => ({
			id: itinerary.id,
			title: `${index + 1}. ${itinerary.title}`
		}))
		return {
			pageHeader,
			pageContent,
		}
	}
}
</script>

<style>
.trip-planner-content > p:last-child {
	margin-bottom: 0;
}
.other-trip-link img {
	transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
}
.other-trip-link:hover img {
	scale: 1.05;
}
</style>
